// src/pages/homecomponents/Mainsite.js — V18 "1999.07.20" home.
//
// Five acts on one scroll: 空 the end sky → 目 the eye → 詩 monologue →
// 論 Tractatus → 扉 the gates. A fixed backdrop cross-fades the CG plates per
// act, and a single WebGL canvas switches its Three.js scene (eye, crosses,
// tentacles) to match the act in view. Hovering a gate lights its cross.

import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import styled, { css } from 'styled-components';
import SceneCanvas from '../../scene/SceneCanvas';
import { Chapter, Seal } from '../../components/ui';
import { color, font, halftone } from '../../theme/tokens';

const ACTS = [
    { key: 'sky', label: '空', mode: 'hero' },
    { key: 'eye', label: '目', mode: 'eye' },
    { key: 'poem', label: '詩', mode: 'poem' },
    { key: 'tractatus', label: '論', mode: 'tractatus' },
    { key: 'gates', label: '扉', mode: 'gates' },
];

const POEM_LINES = [
    '世界は意志の奔流だと、認めよう。',
    '欲望と苦難の波を巻き上げて。',
    'けれど私は岸辺を歩む者でいたい。',
    '「美」と「不条理」という名の、',
    '貝殻を幾つか拾い上げる。',
    '海を渡ろうとは望まず、',
    '恐れゆえに背を向けもせず。',
    'ただ此処で、意識して、',
    '幸福という名の、小さな砂の城を、',
    '築いて、静かに潮汐を待つ。',
];

const GATES = [
    { to: '/announcements', num: '壱', jp: '公告', en: 'NEWS' },
    { to: '/forum', num: '弐', jp: '論壇', en: 'FORUM' },
    { to: '/games', num: '参', jp: '遊戯', en: 'GAMES' },
    { to: '/planner', num: '肆', jp: '計画', en: 'PLANNER' },
];

/* ---------------- backdrop ---------------- */

const Root = styled.div`
    position: relative;
    height: 100%;
    overflow-y: auto;
    overflow-x: hidden;
    scroll-snap-type: y proximity;
    background: ${color.void};
    color: ${color.text};
    font-family: ${font.display};
    scrollbar-width: none;
    &::-webkit-scrollbar { display: none; }
`;

const Fixed = styled.div`
    position: fixed;
    inset: 0;
    pointer-events: none;
`;

const Plate = styled(Fixed)`
    z-index: 0;
    opacity: ${({ $on }) => ($on ? 1 : 0)};
    transition: opacity 1.2s ease;
    ${({ $bg }) => $bg}
`;

const plates = [
    css`
        background: url('/resource/tsuinosora1.webp') center 38% / cover no-repeat;
        filter: brightness(0.8) saturate(1.05);
        inset: -2%;
    `,
    css`
        background:
            linear-gradient(90deg, rgba(7, 2, 5, 0.92) 0%, rgba(7, 2, 5, 0.55) 45%, rgba(7, 2, 5, 0.1) 100%),
            url('/resource/sp0040b.webp') 30% 30% / cover no-repeat;
        filter: brightness(0.55);
    `,
    css`background: radial-gradient(ellipse at 50% 110%, #3a0814 0%, #12030a 45%, ${color.void} 80%);`,
    css`background: radial-gradient(ellipse at 28% 50%, #2a0610 0%, #0d0307 50%, ${color.void} 85%);`,
    css`background: radial-gradient(ellipse at 50% 30%, #2c0710 0%, #0d0307 55%, ${color.void} 90%);`,
];

const CanvasLayer = styled(Fixed)`
    z-index: 1;
    canvas { animation: ts-fade-in 0.9s ease; }
`;

const Vignette = styled(Fixed)`
    z-index: 2;
    background:
        linear-gradient(180deg, rgba(7, 2, 5, 0.55) 0%, transparent 16%, transparent 80%, rgba(7, 2, 5, 0.75) 100%),
        radial-gradient(ellipse at 50% 45%, transparent 45%, rgba(7, 2, 5, 0.7) 100%);
`;

const Halftone = styled(Fixed)`
    z-index: 3;
    opacity: 0.32;
    ${halftone}
`;

/* ---------------- chapter rail ---------------- */

const Rail = styled.nav`
    position: fixed;
    right: 40px;
    top: 50%;
    transform: translateY(-50%);
    z-index: 20;
    display: flex;
    flex-direction: column;
    gap: 10px;
    @media (max-width: 720px) { right: 18px; }
`;

const RailBtn = styled.button`
    all: unset;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 10px;
    min-height: 28px;
    font-family: ${font.display};
    font-size: 13px;
    color: ${({ $on }) => ($on ? color.bloodHi : color.text3)};
    transition: color 0.4s ease;
    &::before {
        content: '';
        width: 6px;
        height: 6px;
        transform: rotate(45deg);
        border: 1px solid currentColor;
        background: ${({ $on }) => ($on ? color.bloodHi : 'transparent')};
        box-shadow: ${({ $on }) => ($on ? '0 0 10px rgba(224, 70, 79, 0.7)' : 'none')};
        transition: all 0.4s ease;
    }
    &:hover { color: ${color.text}; }
    &:focus-visible { outline: 2px solid ${color.bloodHi}; outline-offset: 4px; }
    @media (max-width: 720px) { span { display: none; } }
`;

/* ---------------- acts ---------------- */

const Act = styled.section`
    position: relative;
    z-index: 10;
    min-height: 100%;
    box-sizing: border-box;
    padding: 96px 120px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    scroll-snap-align: start;
    @media (max-width: 900px) { padding: 96px 28px; }
`;

const reveal = css`
    opacity: ${({ $in }) => ($in ? 1 : 0)};
    transform: translateY(${({ $in }) => ($in ? '0' : '18px')});
    transition: opacity 1.1s ease, transform 1.1s ease;
`;

const Vertical = styled.p`
    position: absolute;
    margin: 0;
    writing-mode: vertical-rl;
    font-size: 14px;
    letter-spacing: 0.5em;
    color: ${color.text2};
    &.l { left: 88px; top: 190px; }
    &.r { right: 112px; top: 150px; }
    @media (max-width: 720px) {
        font-size: 12px;
        &.l { left: 32px; top: 140px; }
        &.r { display: none; }
    }
`;

const Hero = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 18px;
    ${reveal};
    h1 {
        margin: 0;
        font-weight: 800;
        font-size: clamp(88px, 12vw, 176px);
        line-height: 1;
        letter-spacing: 0.12em;
        padding-left: 0.12em;
        color: ${color.textBright};
        text-shadow: 0 0 40px rgba(200, 50, 62, 0.55), 0 2px 0 rgba(7, 2, 5, 0.9);
    }
    .rule { width: 320px; max-width: 70vw; height: 1px; background: linear-gradient(90deg, transparent, ${color.blood}, transparent); }
    .en { font-family: ${font.mono}; font-size: 12px; letter-spacing: 0.7em; padding-left: 0.7em; color: ${color.text2}; text-align: center; }
    .sub { font-size: 15px; letter-spacing: 0.4em; color: ${color.text3}; }
    @media (max-width: 720px) {
        h1 { writing-mode: vertical-rl; font-size: 104px; letter-spacing: 0.1em; padding: 0; }
        .rule { display: none; }
        .en { font-size: 10px; letter-spacing: 0.5em; }
    }
`;

const ScrollHint = styled.div`
    position: absolute;
    left: 0;
    right: 0;
    bottom: 44px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    color: ${color.text3};
    font-size: 12px;
    letter-spacing: 0.5em;
    i {
        position: relative;
        width: 1px;
        height: 44px;
        background: rgba(234, 225, 222, 0.14);
        overflow: hidden;
    }
    i::after {
        content: '';
        position: absolute;
        inset: 0;
        background: ${color.bloodHi};
        animation: ts-drip 2.4s ease-in infinite;
    }
`;

const EyeText = styled.div`
    max-width: 760px;
    ${reveal};
    h2 {
        margin: 40px 0 0;
        font-weight: 800;
        font-size: clamp(40px, 5vw, 72px);
        letter-spacing: 0.14em;
        color: ${color.textBright};
        text-shadow: 0 0 30px rgba(200, 50, 62, 0.5);
    }
    p { margin: 32px 0 0; font-size: 18px; letter-spacing: 0.5em; color: ${color.text2}; }
`;

const Poem = styled.div`
    align-self: center;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 56px;
    .lines {
        writing-mode: vertical-rl;
        height: min(560px, 68vh);
    }
    .lines > * { margin: 0 0 0 30px; }
    .lines p {
        font-size: min(22px, 2.4vh);
        line-height: 1;
        letter-spacing: 0.32em;
        color: ${color.text};
        opacity: 0;
        transition: opacity 1s ease;
    }
    .lines p.hi { color: ${color.textBright}; }
    @media (max-width: 900px) {
        .lines { height: 420px; }
        .lines > * { margin-left: 16px; }
        .lines p { font-size: 15px; letter-spacing: 0.2em; }
    }
`;

const Tract = styled.div`
    margin-left: auto;
    width: min(680px, 100%);
    display: flex;
    flex-direction: column;
    gap: 30px;
    ${reveal};
    p { margin: 0; }
    .credo { display: flex; align-items: baseline; gap: 28px; font-size: 26px; letter-spacing: 0.16em; line-height: 1.7; color: ${color.text}; }
    .credo b { font-family: ${font.mono}; font-weight: 400; font-size: 13px; letter-spacing: 0.1em; color: ${color.bloodHi}; min-width: 56px; }
    .div { height: 1px; margin: 16px 0; background: linear-gradient(90deg, rgba(200, 50, 62, 0.5), transparent); }
    .last { font-weight: 800; font-size: 44px; letter-spacing: 0.3em; color: ${color.textBright}; text-shadow: 0 0 30px rgba(200, 50, 62, 0.45); }
    @media (max-width: 900px) {
        margin: 42vh 0 0;
        .credo { font-size: 18px; gap: 16px; }
        .last { font-size: 30px; }
    }
`;

const Gates = styled.div`
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 32px;
    width: 100%;
    max-width: 1120px;
    margin: 28vh auto 0;
    @media (max-width: 900px) { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 14px; margin-top: 30vh; }
`;

const Gate = styled(Link)`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 14px;
    padding: 36px 0 30px;
    border: 1px solid ${color.line};
    background-color: rgba(15, 5, 9, 0.6);
    color: ${color.text};
    text-decoration: none;
    transition: transform 0.4s ease, border-color 0.4s ease, background-color 0.4s ease;
    .n { font-size: 14px; color: ${color.bloodHi}; letter-spacing: 0.2em; }
    .jp { font-weight: 800; font-size: 40px; letter-spacing: 0.24em; padding-left: 0.24em; }
    .en { font-family: ${font.mono}; font-size: 11px; letter-spacing: 0.5em; padding-left: 0.5em; color: ${color.text3}; }
    &:hover, &:focus-visible { transform: translateY(-6px); border-color: ${color.bloodHi}; background-color: rgba(23, 10, 15, 0.9); outline: none; }
    @media (max-width: 900px) { padding: 24px 0 20px; .jp { font-size: 28px; } }
`;

const Foot = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    flex-wrap: wrap;
    gap: 28px 40px;
    margin-top: 72px;
    font-family: ${font.mono};
    font-size: 12px;
    letter-spacing: 0.3em;
    a { color: ${color.text2}; text-decoration: none; }
    a:hover { color: ${color.bloodHi}; }
    i { width: 4px; height: 4px; transform: rotate(45deg); background: ${color.blood}; }
`;

export default function MainSite() {
    const rootRef = useRef(null);
    const sectionRefs = useRef([]);
    const sceneRef = useRef(null);
    const [active, setActive] = useState(0);
    const [seen, setSeen] = useState(() => new Set([0]));

    useEffect(() => {
        const root = rootRef.current;
        if (!root) return undefined;
        const io = new IntersectionObserver((entries) => {
            entries.forEach((e) => {
                if (!e.isIntersecting) return;
                const i = Number(e.target.dataset.act);
                setActive(i);
                setSeen((s) => (s.has(i) ? s : new Set([...s, i])));
            });
        }, { root, threshold: 0.55 });
        sectionRefs.current.forEach((el) => el && io.observe(el));
        return () => io.disconnect();
    }, []);

    const bind = (i) => (el) => { sectionRefs.current[i] = el; };
    const jump = (i) => sectionRefs.current[i]?.scrollIntoView({ behavior: 'smooth', block: 'start' });

    return (
        <Root ref={rootRef}>
            {plates.map((bg, i) => <Plate key={i} $bg={bg} $on={active === i} aria-hidden="true" />)}
            <CanvasLayer aria-hidden="true">
                <SceneCanvas key={ACTS[active].mode} ref={sceneRef} mode={ACTS[active].mode} />
            </CanvasLayer>
            <Vignette aria-hidden="true" />
            <Halftone aria-hidden="true" />

            <Rail aria-label="章节">
                {ACTS.map((a, i) => (
                    <RailBtn key={a.key} type="button" $on={active === i} onClick={() => jump(i)} aria-label={`第 ${i + 1} 幕 ${a.label}`} aria-current={active === i}>
                        <span>{a.label}</span>
                    </RailBtn>
                ))}
            </Rail>

            {/* 序 — 空 */}
            <Act ref={bind(0)} data-act="0" style={{ alignItems: 'center' }}>
                <Vertical className="l">僕は・・・君に・・・おとぎ話を・・・してあげよう・・・</Vertical>
                <Vertical className="r">「世界は終わる！確実に終わる！」</Vertical>
                <Hero $in={seen.has(0)}>
                    <h1>終ノ空</h1>
                    <div className="rule" />
                    <div className="en">1999.07.20 · THE END OF THE SKY</div>
                    <div className="sub">──僕は終ノ空に至るんだ</div>
                </Hero>
                <ScrollHint aria-hidden="true"><span>下ヘ、堕チル</span><i /></ScrollHint>
            </Act>

            {/* 第一章 — 視線 */}
            <Act ref={bind(1)} data-act="1">
                <EyeText $in={seen.has(1)}>
                    <Chapter>第一章──視線</Chapter>
                    <h2>空ニハ、目ガ在ル。</h2>
                    <p>──ズット、視ラレテイル。</p>
                </EyeText>
            </Act>

            {/* 第二章 — 独白 */}
            <Act ref={bind(2)} data-act="2">
                <Poem>
                    <Chapter>第二章──独白</Chapter>
                    <div className="lines">
                        {POEM_LINES.map((l, i) => (
                            <p
                                key={l}
                                className={i % 3 === 0 ? 'hi' : ''}
                                style={{ marginTop: (i % 3) * 36, opacity: seen.has(2) ? 1 : 0, transitionDelay: `${i * 0.18}s` }}
                            >
                                {l}
                            </p>
                        ))}
                        <div style={{ marginTop: 250 }}><Seal $size={56} style={{ fontSize: 20 }}>深淵</Seal></div>
                    </div>
                </Poem>
            </Act>

            {/* 第三章 — 論考 */}
            <Act ref={bind(3)} data-act="3">
                <Tract $in={seen.has(3)}>
                    <Chapter>第三章──論考</Chapter>
                    <p className="credo"><b>6.4311</b><span>死は人生の出来事ではない。人は死を体験しない。</span></p>
                    <p className="credo"><b>7</b><span>語りえぬことについては、沈黙しなくてはならない</span></p>
                    <div className="div" />
                    <p className="last">幸福ニ生キヨ。</p>
                </Tract>
            </Act>

            {/* 終章 — 扉 */}
            <Act ref={bind(4)} data-act="4">
                <div style={{ display: 'flex', justifyContent: 'center', position: 'absolute', left: 0, right: 0, top: 96 }}>
                    <Chapter>終章──扉</Chapter>
                </div>
                <Gates>
                    {GATES.map((g, i) => (
                        <Gate
                            key={g.to}
                            to={g.to}
                            onMouseEnter={() => sceneRef.current && sceneRef.current.setHighlight(i)}
                            onMouseLeave={() => sceneRef.current && sceneRef.current.setHighlight(-1)}
                            onFocus={() => sceneRef.current && sceneRef.current.setHighlight(i)}
                            onBlur={() => sceneRef.current && sceneRef.current.setHighlight(-1)}
                        >
                            <span className="n">{g.num}</span>
                            <span className="jp">{g.jp}</span>
                            <span className="en">{g.en}</span>
                        </Gate>
                    ))}
                </Gates>
                <Foot>
                    <a href="mailto:abyss.gate@outlook.com">EMAIL</a>
                    <i />
                    <a href="https://github.com/abyss464" target="_blank" rel="noopener noreferrer">GITHUB</a>
                    <i />
                    <Link to="/terms">条款</Link>
                    <Link to="/privacy">隐私</Link>
                </Foot>
            </Act>
        </Root>
    );
}
